/**
 * <notify-me-form>
 *
 * Back-in-stock email capture backed by Klaviyo's public *client* Back in Stock
 * API. No private key and no backend: the browser POSTs the shopper's email and
 * the Shopify variant id, Klaviyo stores the subscription, and Klaviyo's own
 * "Back in Stock" flow sends the restock email once Shopify syncs inventory.
 *
 * Rendered by snippets/notify-me-form.liquid.
 */
if (!customElements.get('notify-me-form')) {
  customElements.define(
    'notify-me-form',
    class NotifyMeForm extends HTMLElement {
      static BACK_IN_STOCK_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
      static SUBSCRIPTIONS_URL = 'https://a.klaviyo.com/client/subscriptions';
      static MIN_TIME_ON_FORM = 2000;

      constructor() {
        super();
        this.form = this.querySelector('form');
        this.emailInput = this.querySelector('input[type="email"]');
        this.variantSelect = this.querySelector('select[name="variant_id"]');
        this.consentInput = this.querySelector('input[name="consent"]');
        this.marketingInput = this.querySelector('input[name="marketing_opt_in"]');
        this.honeypot = this.querySelector('input[name="notify_me_company"]');
        this.submitButton = this.querySelector('.notify-me__submit');
        this.status = this.querySelector('.notify-me__status');
        this.submitted = new Set();
        this.loading = false;
      }

      connectedCallback() {
        // <modal-dialog> reparents itself to document.body, so card instances
        // disconnect and reconnect. Binding twice would double-submit.
        if (this.initialized) return;
        this.initialized = true;

        this.removeAttribute('hidden');

        this.companyId = this.constructor.resolveCompanyId(this.dataset.companyId);
        if (!this.companyId) {
          console.warn('[notify-me] No Klaviyo public API key. Set it in Theme settings > Klaviyo.');
          this.remove();
          return;
        }

        this.renderedAt = Date.now();
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.consentInput?.addEventListener('change', () => this.syncSubmitState());
        this.syncSubmitState();

        // Only the product page has a <product-info> ancestor; cards do not.
        this.productInfo = this.closest('product-info');
        if (this.productInfo) this.bindVariantChange();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        if (this.variantInput && this.boundVariantInputChange) {
          this.variantInput.removeEventListener('change', this.boundVariantInputChange);
        }
      }

      /**
       * The theme setting is the source of truth. The script-tag sniff is a
       * fallback for when settings_data.json has drifted from the live theme.
       */
      static resolveCompanyId(fromSetting) {
        if (fromSetting) return fromSetting.trim();

        const tag = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
        if (!tag) return null;

        try {
          const url = new URL(tag.src, window.location.href);
          // Two embed shapes in the wild:
          //   /onsite/js/klaviyo.js?company_id=XXXXXX
          //   /onsite/js/XXXXXX/klaviyo.js   <-- what the Shopify app embed uses
          const fromQuery = url.searchParams.get('company_id');
          if (fromQuery) return fromQuery;

          const fromPath = url.pathname.match(/\/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js$/);
          return fromPath ? fromPath[1] : null;
        } catch (error) {
          return null;
        }
      }

      /* ---------- variant tracking (product page only) ---------- */

      bindVariantChange() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;
          this.applyVariant(event.data.variant);
        });

        // variantChange is NOT published when the option combination resolves to
        // no variant -- product-info.js returns early into setUnavailable(). But
        // updateVariantInputs() runs first and always dispatches a bubbling
        // `change` on the hidden input, with an empty value.
        this.variantInput = this.productInfo.querySelector('input[name="id"].product-variant-id');
        this.boundVariantInputChange = () => {
          if (this.variantInput.value === '') this.applyVariant(null);
        };
        this.variantInput?.addEventListener('change', this.boundVariantInputChange);
      }

      applyVariant(variant) {
        if (!variant) {
          this.dataset.variantId = '';
          this.toggle(false);
          return;
        }

        const changed = String(variant.id) !== this.dataset.variantId;
        this.dataset.variantId = variant.id;
        if (changed) this.resetState();

        // variant.available already accounts for inventory_policy 'continue'.
        this.toggle(variant.available === false);
      }

      toggle(show) {
        this.classList.toggle('hidden', !show);
      }

      resetState() {
        this.form.querySelectorAll('[data-notify-hidden]').forEach((element) => {
          element.hidden = false;
          delete element.dataset.notifyHidden;
        });
        this.setStatus('', null);
        this.toggleLoading(false);
        this.syncSubmitState();
      }

      /** The submit button stays disabled until the terms checkbox is ticked. */
      consentGiven() {
        return this.consentInput ? this.consentInput.checked : true;
      }

      syncSubmitState() {
        if (this.loading) return;
        this.submitButton.disabled = !this.consentGiven();
      }

      /* ---------- submission ---------- */

      async onSubmit(event) {
        event.preventDefault();
        if (this.loading) return;

        // Bots: show success, send nothing, give no signal.
        const trapped = this.honeypot ? this.honeypot.value !== '' : false;
        const tooFast = Date.now() - this.renderedAt < this.constructor.MIN_TIME_ON_FORM;
        if (trapped || tooFast) {
          this.showSuccess();
          return;
        }

        const email = this.emailInput.value.trim();
        const variantId = this.variantSelect?.value || this.dataset.variantId;

        if (!variantId) {
          this.setStatus(window.notifyMeStrings.errorVariant, 'error');
          return;
        }

        if (email === '' || !this.emailInput.checkValidity()) {
          this.setStatus(window.notifyMeStrings.errorEmail, 'error');
          this.emailInput.focus();
          return;
        }

        // Backstop: the button is disabled without consent, but never trust that alone.
        if (!this.consentGiven()) {
          this.setStatus(window.notifyMeStrings.errorConsent, 'error');
          this.consentInput.focus();
          return;
        }

        const key = `${email.toLowerCase()}::${variantId}`;
        if (this.submitted.has(key)) {
          this.showSuccess();
          return;
        }

        this.toggleLoading(true);
        this.setStatus(window.notifyMeStrings.sending, null);

        try {
          const response = await fetch(
            `${this.constructor.BACK_IN_STOCK_URL}?company_id=${encodeURIComponent(this.companyId)}`,
            {
              method: 'POST',
              mode: 'cors',
              // Adding any further header widens the CORS preflight. Don't.
              headers: {
                'Content-Type': 'application/json',
                revision: this.dataset.revision,
              },
              body: JSON.stringify({
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: {
                    profile: { data: { type: 'profile', attributes: { email } } },
                    channels: ['EMAIL'],
                  },
                  relationships: {
                    variant: {
                      data: {
                        type: 'catalog-variant',
                        id: `$shopify:::$default:::${variantId}`,
                      },
                    },
                  },
                },
              }),
            }
          );

          if (response.status === 202) {
            this.submitted.add(key);

            if (this.marketingInput?.checked) {
              // Fire and forget: a marketing-consent failure must not fail the signup.
              this.subscribeToMarketing(email).catch((error) => {
                console.warn('[notify-me] marketing subscription failed', error);
              });
            }

            this.showSuccess();
            return;
          }

          if (response.status === 429) {
            this.setStatus(window.notifyMeStrings.errorRateLimit, 'error');
          } else {
            let detail = '';
            try {
              const body = await response.json();
              detail = body?.errors?.[0]?.detail || '';
            } catch (error) {
              /* Klaviyo often replies with no body. */
            }
            // A 404 here usually means the `revision` header has aged out.
            console.warn('[notify-me] Klaviyo returned', response.status, detail);
            this.setStatus(window.notifyMeStrings.errorGeneric, 'error');
          }
        } catch (error) {
          // TypeError: Failed to fetch -- ad blocker, CORS, or offline.
          console.warn('[notify-me] request blocked or failed', error);
          this.setStatus(window.notifyMeStrings.errorBlocked, 'error');
        } finally {
          this.toggleLoading(false);
        }
      }

      /**
       * A back-in-stock subscription is NOT marketing consent in Klaviyo: it
       * neither requires nor records it. Opting in is a separate, optional call.
       */
      subscribeToMarketing(email) {
        const listId = this.dataset.listId;
        if (!listId) return Promise.resolve();

        return fetch(
          `${this.constructor.SUBSCRIPTIONS_URL}?company_id=${encodeURIComponent(this.companyId)}`,
          {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              revision: this.dataset.revision,
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Back in stock form',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                      },
                    },
                  },
                },
                relationships: { list: { data: { type: 'list', id: listId } } },
              },
            }),
          }
        );
      }

      /* ---------- ui state ---------- */

      toggleLoading(state) {
        this.loading = state;
        this.submitButton.classList.toggle('loading', state);
        this.submitButton.setAttribute('aria-busy', state);
        this.submitButton.disabled = state || !this.consentGiven();
      }

      setStatus(message, type) {
        this.status.textContent = message;
        this.status.classList.toggle('notify-me__status--error', type === 'error');
        this.status.classList.toggle('notify-me__status--success', type === 'success');
      }

      showSuccess() {
        this.form
          .querySelectorAll('.notify-me__intro, .field, .notify-me__consent, .notify-me__submit')
          .forEach((element) => {
            element.hidden = true;
            element.dataset.notifyHidden = 'true';
          });
        this.setStatus(window.notifyMeStrings.success, 'success');
        this.status.focus();
      }
    }
  );
}
